/**
 * Totals for the Reports tabs, from the flat rows the server sends.
 *
 * Totalled here rather than on the server so the region filter is instant and
 * every exported figure can be traced to the rows beneath it. Pure: no React,
 * no network.
 */

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Seed's fiscal quarters: Q1 Sep-Nov, Q2 Dec-Feb, Q3 Mar-May, Q4 Jun-Aug.
 * Q2 spans the calendar new year, so it is always read by month, never by date.
 */
export const QUARTERS = [
  { q: 1, months: [9, 10, 11], label: 'Sep - Nov' },
  { q: 2, months: [12, 1, 2], label: 'Dec - Feb' },
  { q: 3, months: [3, 4, 5], label: 'Mar - May' },
  { q: 4, months: [6, 7, 8], label: 'Jun - Aug' },
];

export const quarterOf = (month) => QUARTERS.find(x => x.months.includes(Number(month)))?.q || null;

/** Rows in one fiscal quarter; 'all' (or nothing) keeps the whole year. */
export const inQuarter = (rows, quarter) => (!quarter || quarter === 'all'
  ? (rows || [])
  : (rows || []).filter(r => quarterOf(r.month) === Number(quarter)));

/** "Q2 FY26 (Dec 2025 - Feb 2026)" */
export const quarterLabel = (q, fy) => {
  const quarter = QUARTERS.find(x => x.q === Number(q));
  if (!quarter) return '';
  const [first, , last] = quarter.months.map(m => ({ m, y: m >= 9 ? fy - 1 : fy }));
  const mon = (m) => new Date(2000, m - 1, 1).toLocaleString('en-US', { month: 'short' });
  return `Q${q} FY${String(fy).slice(-2)} (${mon(first.m)} ${first.y} - ${mon(last.m)} ${last.y})`;
};

export const regionsOf = (rows) => [...new Set((rows || []).map(r => r.region).filter(Boolean))].sort();

export const inRegion = (rows, region) => (!region || region === 'all'
  ? (rows || [])
  : (rows || []).filter(r => r.region === region));

/**
 * Funding by PTAE code, largest first, with money that has no code last.
 *
 * A code can be defined in more than one region, so its description is taken
 * from the definition in a region the money was actually spent in.
 */
export const fundingByCode = (lines, ptaes = []) => {
  const list = lines || [];
  const grand = list.reduce((t, l) => t + (Number(l.amount) || 0), 0);
  const by = new Map();
  for (const l of list) {
    const code = l.code || '';
    const b = by.get(code) || { code, claims: new Set(), regions: new Set(), amount: 0 };
    b.claims.add(l.claimRef);
    b.regions.add(l.region);
    b.amount += Number(l.amount) || 0;
    by.set(code, b);
  }
  return [...by.values()]
    .map(b => {
      const defs = (ptaes || []).filter(p => p.code === b.code);
      const def = defs.find(p => b.regions.has(p.region)) || defs[0] || null;
      return {
        code: b.code,
        description: def?.description || null,
        fundName: def?.fundName || null,
        suspended: !!def?.suspended,
        region: [...b.regions].sort().join(', '),
        claims: b.claims.size,
        amount: round2(b.amount),
        share: grand ? b.amount / grand : 0,
      };
    })
    .sort((a, b) => {
      if (!a.code !== !b.code) return a.code ? -1 : 1;
      return b.amount - a.amount;
    });
};


/* ------------------------------------------------------------ final claims */
// Rows from GET /reports/claims: one per claim that has reached its FINAL
// approval, { claimId, claimType: 'expense'|'perdiem', claimant: { id, name,
// region, position }, date, amountUsd, reportId, reportLabel }.

export const NO_POSITION = 'No position recorded';

const isPerDiem = (c) => c.claimType === 'perdiem';
const amountOf = (c) => Number(c.amountUsd) || 0;

/** Headline figures for a set of final claims. */
export const claimTotals = (claims) => {
  const list = claims || [];
  const perDiem = list.filter(isPerDiem);
  return {
    total: round2(list.reduce((t, c) => t + amountOf(c), 0)),
    perDiem: round2(perDiem.reduce((t, c) => t + amountOf(c), 0)),
    expense: round2(list.filter(c => !isPerDiem(c)).reduce((t, c) => t + amountOf(c), 0)),
    claims: list.length,
    perDiemClaims: perDiem.length,
    expenseClaims: list.length - perDiem.length,
    claimants: new Set(list.map(c => c.claimant?.id)).size,
  };
};

/**
 * One line per region, largest first. `regions` are listed even with nothing
 * in them, so a region in scope that spent nothing shows as a zero rather
 * than silently missing.
 */
export const claimsByRegion = (claims, regions = []) => {
  const list = claims || [];
  const names = [...new Set([...regions, ...list.map(c => c.claimant?.region).filter(Boolean)])];
  return names
    .map(region => ({ region, ...claimTotals(list.filter(c => c.claimant?.region === region)) }))
    .sort((a, b) => b.total - a.total || a.region.localeCompare(b.region));
};

/**
 * One line per job position (User.position), largest first. Positions are
 * trimmed and grouped case-insensitively, since the field is typed freely:
 * "Coach" and "coach " are one position. The label shown is the spelling
 * seen most often.
 */
export const claimsByPosition = (claims) => {
  const by = new Map();
  for (const c of claims || []) {
    const raw = String(c.claimant?.position || '').trim().replace(/\s+/g, ' ');
    const key = raw.toLowerCase();
    const g = by.get(key) || { key, spellings: new Map(), claims: [] };
    if (raw) g.spellings.set(raw, (g.spellings.get(raw) || 0) + 1);
    g.claims.push(c);
    by.set(key, g);
  }
  return [...by.values()]
    .map(g => ({
      position: g.key
        ? [...g.spellings.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0][0]
        : NO_POSITION,
      recorded: !!g.key,
      ...claimTotals(g.claims),
    }))
    .sort((a, b) => b.total - a.total || a.position.localeCompare(b.position));
};

/**
 * One line per claimant, largest first. `list` holds their claims, newest
 * first, for the expanded row; `claims` is the count, as elsewhere.
 */
export const claimsByPerson = (claims) => {
  const by = new Map();
  for (const c of claims || []) {
    const id = c.claimant?.id ?? c.claimant?.name ?? 'unknown';
    const p = by.get(id) || {
      id,
      name: c.claimant?.name || 'Unknown claimant',
      region: c.claimant?.region || '',
      position: String(c.claimant?.position || '').trim(),
      list: [],
    };
    p.list.push(c);
    by.set(id, p);
  }
  return [...by.values()]
    .map(p => ({
      ...p,
      ...claimTotals(p.list),
      list: [...p.list].sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))),
    }))
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
};
